import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

import { OverlayTrigger, Tooltip, Modal, Button } from 'react-bootstrap';
import { createAdmin, updateAdmin, fetchAdmin, fetchAdmins, clearCurrentAdmin } from '../../../../store/slices/adminSlice';
import { fetchPractice } from '../../../../store/slices/practiceSlice';
import { useBreadcrumb } from '../../../../contexts/BreadcrumbContext';
import adminService from '../../../../services/adminService';
import { toast } from 'react-toastify';
import { VALIDATION_SUMMARY_MESSAGE, preserveAvailabilityErrors, clearFieldError, validateRequiredPhone, updateFieldErrorOnChange } from '../../../../utils/formValidation';
import { formatUSPhoneNumber, extractPhoneDigits, getPhoneTypingError } from '../../../../utils/phoneUtils';
import PhoneNumberInput from '../../../../components/Common/PhoneNumberInput/PhoneNumberInput';
import { sanitizeEmailInput, getEmailValidationError } from '../../../../utils/emailValidation';
import { sanitizeUsernameInput, getUsernameFormatError, USERNAME_MAX_LENGTH } from '../../../../utils/usernameValidation';
import useUsernameEmailAvailability from '../../../../hooks/useUsernameEmailAvailability';
import { normalizeAdminStatus } from '../../../../utils/adminStatus';
import './AddAdmin.css';
import '../../ProviderManagement/AddProvider/AddProvider.css';
import '../../PatientManagement/AddPatient/AddPatientModal.css';
import { validatePassword, applyPasswordServerFieldError } from '../../../../utils/passwordValidation';
import { TIME_ZONES, TITLES, US_STATES, normalizeTimeZone } from '../../../../utils/constants';
import AddFormModel from '../../../../components/Common/AddFormModel/AddFormModel';
import FormActionButtons from '../../../../components/Common/FormActionButtons/FormActionButtons';
import FormDetailSkeleton from '../../../../components/SkeletonLoader/FormDetailSkeleton';

const AddAdmin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { practiceId } = useParams();
  const [searchParams] = useSearchParams();

  const { isCreating, isUpdating, error, currentAdmin } = useSelector(state => state.admin);
  const { currentPractice } = useSelector(state => state.practice ?? {});
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [systemAdmin, setSystemAdmin] = useState(null);
  const [loadingAdmin, setLoadingAdmin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [showValidationModal, setShowValidationModal] = useState(false);

  // Check if we're in edit mode
  const editId = searchParams.get('editId');
  const isEditMode = Boolean(editId);
  const isSystemAdmin = !practiceId; // System admin if no practiceId in route
  const excludeUserId = isEditMode ? (editId ?? systemAdmin?.id ?? currentAdmin?.id ?? null) : null;
  const [loadedCredentials, setLoadedCredentials] = useState({ username: '', email: '' });
  const {
    scheduleUsernameCheck,
    scheduleEmailCheck,
    handleUsernamePaste,
    handleUsernameBlur,
    handleEmailPaste,
    ensureUsernameAvailable,
    ensureEmailAvailable,
  } = useUsernameEmailAvailability({
    excludeUserId,
    originalUsername: loadedCredentials.username,
    originalEmail: loadedCredentials.email,
  });

  // Fetch admin data when in edit mode
  useEffect(() => {
    if (isEditMode && editId) {
      if (isSystemAdmin) {
        // Fetch system admin
        setSystemAdmin(null);
        setLoadingAdmin(true);
        adminService.getSystemAdmin(editId)
          .then(response => {
            if (response.data && response.data.success) {
              setSystemAdmin(response.data.data);
            }
          })
          .catch(error => {
            console.error('Error fetching system admin:', error);
          })
          .finally(() => {
            setLoadingAdmin(false);
          });
      } else {
        // Fetch practice admin
        dispatch(clearCurrentAdmin());
        dispatch(fetchAdmin({ practiceId, adminId: editId }));
      }
    }
  }, [isEditMode, editId, practiceId, isSystemAdmin, dispatch]);

  // Fetch practice data if not already state
  useEffect(() => {
    if (practiceId && (!currentPractice || String(currentPractice.id) !== String(practiceId))) {
      dispatch(fetchPractice(practiceId));
    }
  }, [practiceId, currentPractice, dispatch]);

  useEffect(() => {
    return () => {
      clearBreadcrumbs();
    };
  }, [clearBreadcrumbs]);

  useEffect(() => {
    if (isSystemAdmin) {
      updateBreadcrumbs([
        { label: 'Admin Users', path: '/admin' },
        { label: isEditMode ? 'Edit Admin' : 'Add Admin', path: null, active: true }
      ]);
    } else {
      const practiceName = currentPractice?.practice_name || 'Practice';
      const fromDashboard = !!location.state?.fromDashboard;
      const rootLabel = fromDashboard ? 'Dashboard' : 'Practices';
      const rootPath = fromDashboard ? '/dashboard' : '/practices';
      const dashboardTabSuffix = fromDashboard ? '&fromDashboard=true' : '';

      updateBreadcrumbs([
        { label: rootLabel, path: rootPath },
        { label: practiceName, path: `${fromDashboard ? '/dashboard' : '/practices'}/${practiceId}?tab=practice${dashboardTabSuffix}` },
        { label: isEditMode ? 'Edit Practice Admin' : 'Add Practice Admin', path: null, active: true }
      ]);
    }

  }, [isSystemAdmin, isEditMode, practiceId, currentPractice, updateBreadcrumbs]);

  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    lastName: '',
    middleInitial: '',
    email: '',
    phone: '',
    cellPhone: '',
    fax: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '', // Full Name
    stateId: null, // backend ID
    zipCode: '',
    username: '',
    password: '',
    confirmPassword: '',
    status: 'Active',
    twoWayAuth: 'Disabled',
    timeZone: 'America/New_York'

  });

  const [initialFormData, setInitialFormData] = useState(null);

  const isChanged = React.useMemo(() => {
    if (!isEditMode || !initialFormData) return true; // Always enabled in create mode
    if (formData.password?.trim() || formData.confirmPassword?.trim()) return true;
    // Compare formData with initialFormData (excluding passwords which are handled separately)
    return JSON.stringify({ ...formData, password: '', confirmPassword: '' }) !== 
           JSON.stringify({ ...initialFormData, password: '', confirmPassword: '' });
  }, [formData, initialFormData, isEditMode]);

  // Populate form data when admin is loaded
  useEffect(() => {
    if (isEditMode) {
      const adminData = isSystemAdmin ? systemAdmin : currentAdmin;
      if (adminData && String(adminData.id) === String(editId)) {
        const mappedData = {
          title: (adminData.title_id !== null && adminData.title_id !== undefined) 
            ? String(adminData.title_id) 
            : (TITLES.find(t => t.label === adminData.title)?.id ? String(TITLES.find(t => t.label === adminData.title).id) : ''),
          firstName: adminData.first_name || '',
          lastName: adminData.last_name || '',
          middleInitial: adminData.middle_name || adminData.middle_initial || '',
          email: adminData.email || '',
          addressLine1: adminData.address_line_1 || '',
          addressLine2: adminData.address_line_2 || '',
          city: adminData.city || '',
          state: US_STATES.find(s => s.id === adminData.state_id)?.name || '',
          stateId: adminData.state_id || null,
          zipCode: adminData.zip_code ? String(adminData.zip_code) : '',
          phone: formatPhoneNumber(adminData.cell_phone || ''),
          cellPhone: formatPhoneNumber(adminData.phone || adminData.home_phone || ''),
          fax: formatPhoneNumber(adminData.fax || ''),
          username: adminData.username || adminData.user?.username || '',
          status: normalizeAdminStatus(adminData.status),
          twoWayAuth: (adminData.is_two_way_auth === 1 || adminData.is_two_way_auth === true || adminData.two_way_auth === 'Enabled' || adminData.two_way_authentication === 'Enabled') ? 'Enabled' : 'Disabled',
          timeZone: normalizeTimeZone(adminData.time_zone || adminData.timeZone || adminData.timezone) || '',
          password: '',
          confirmPassword: ''
        };
        setFormData(mappedData);
        setInitialFormData(mappedData);
        setLoadedCredentials({
          username: mappedData.username || '',
          email: mappedData.email || '',
        });
      }
    }
  }, [isEditMode, currentAdmin, systemAdmin, isSystemAdmin, editId]);
  // Helper for phone formatting (XXX) XXX - XXXX
  const formatPhoneNumber = formatUSPhoneNumber;

  // Helper for email validation
  const validateEmail = (email) => !getEmailValidationError(email, { required: false }) && String(email || '').trim() !== '';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    clearFieldError(setErrors, name);
    let newErrors = { ...errors };

    // Clear existing error for current field before re-validating
    delete newErrors[name];

    // 1. Phone Number Masking & Validation
    if (['phone', 'cellPhone', 'fax'].includes(name)) {
      const formatted = value;
      const digits = extractPhoneDigits(formatted);
      setFormData((prev) => ({ ...prev, [name]: formatted }));

      const typingError = getPhoneTypingError(digits, {
        required: name === 'cellPhone',
        requiredLabel: name === 'phone' ? 'Home Phone' : name === 'cellPhone' ? 'Cell Phone' : 'Fax',
      });

      setErrors((prev) => {
        const next = { ...prev };
        if (typingError) next[name] = typingError;
        else delete next[name];
        return next;
      });
      return;
    }

    // 1.1 Zip Code Validation (Prevention)
    if (name === 'zipCode') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 5) {
        setFormData(prev => ({ ...prev, [name]: numericValue }));
        if (numericValue.length > 0 && numericValue.length < 5) {
          newErrors.zipCode = 'Zip code must be exactly 5 digits';
        } else {
          delete newErrors.zipCode;
        }
        setErrors(newErrors);
      }
      return;
    }

    // 2. Strict Name Validation
    if (['firstName', 'lastName', 'middleInitial'].includes(name)) {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return; 
      }
      if (value.length > 20) {
        return; 
      }
    }

    // 2.1 State Sync
    if (name === 'state') {
      const selectedState = US_STATES.find(s => s.name === value);
      setFormData(prev => ({
        ...prev,
        state: value,
        stateId: selectedState ? selectedState.id : null
      }));
      setErrors(newErrors);
      return;
    }

    // 3. Password Validation (Real-time)
    if (name === 'password') {
      if (!value) {
        setErrors(prev => {
          const { password, ...rest } = prev;
          return rest;
        });
      } else {
        const userData = {
          firstName: formData.firstName,
          lastName: formData.lastName
        };
        const validation = validatePassword(value, userData);
        if (!validation.isValid) {
          setErrors(prev => ({ ...prev, password: validation.error }));
        } else {
          setErrors(prev => {
            const { password, ...rest } = prev;
            return rest;
          });
        }
      }
    }

    // City Validation (Alpha only)
    if (name === 'city') {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return;
      }
    }

    // 4. Email Validation (Real-time + Character Filtering)
    if (name === 'email') {
      const cleanedValue = sanitizeEmailInput(value);
      setFormData(prev => ({ ...prev, email: cleanedValue }));

      const emailErr = getEmailValidationError(cleanedValue);
      if (emailErr) {
        setErrors(prev => ({ ...prev, email: emailErr }));
      } else {
        setErrors(prev => {
          const { email, ...rest } = prev;
          return rest;
        });
        scheduleEmailCheck(cleanedValue, setErrors);
      }
      return;
    }

    // Username validation (letters, numbers, ., _)
    if (name === 'username') {
      const cleanedValue = sanitizeUsernameInput(value);
      setFormData(prev => ({ ...prev, username: cleanedValue }));
      const formatError = getUsernameFormatError(cleanedValue);
      if (!cleanedValue) {
        setErrors(prev => {
          const { username, ...rest } = prev;
          return rest;
        });
      } else if (formatError) {
        setErrors(prev => ({ ...prev, username: formatError }));
      } else {
        setErrors(prev => {
          const { username, ...rest } = prev;
          return rest;
        });
        scheduleUsernameCheck(cleanedValue, setErrors);
      }
      return;
    }

    if (name === 'confirmPassword') {
      if (!value && !isEditMode) {
        newErrors.confirmPassword = 'Confirm Password is required';
      } else if (formData.password && value !== formData.password) {
        newErrors.confirmPassword = 'Passwords do not match';
      } else {
        delete newErrors.confirmPassword;
      }
    }

    const fieldLabels = {
      firstName: 'First Name',
      lastName: 'Last Name',
      username: 'Username',
      addressLine1: 'Address Line 1',
      city: 'City',
      state: 'State',
      zipCode: 'Zip Code',
      timeZone: 'Time Zone',
      twoWayAuth: 'Two Way Authentication'

    };

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name !== 'password' && name !== 'email') {
      updateFieldErrorOnChange(setErrors, name, value, {
        required: Boolean(fieldLabels[name]),
        requiredMessage: fieldLabels[name] ? `${fieldLabels[name]} is required` : null,
      });
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName?.trim()) newErrors.firstName = 'First Name is required';
    if (!formData.lastName?.trim()) newErrors.lastName = 'Last Name is required';
    if (!formData.username?.trim()) {
      newErrors.username = 'Username is required';
    } else {
      const formatError = getUsernameFormatError(formData.username);
      if (formatError) newErrors.username = formatError;
    }
    // if (!formData.timeZone) newErrors.timeZone = 'Time Zone is required';
    if (!formData.twoWayAuth) newErrors.twoWayAuth = 'Two Way Authentication is required';


    // Password validation
    if (!isEditMode) {
      if (!formData.password?.trim()) {
        newErrors.password = 'Password is required';
      } else {
        const userData = { firstName: formData.firstName, lastName: formData.lastName };
        const validation = validatePassword(formData.password, userData);
        if (!validation.isValid) newErrors.password = validation.error;
      }
    } else if (formData.password?.trim()) {
      const userData = { firstName: formData.firstName, lastName: formData.lastName };
      const validation = validatePassword(formData.password, userData);
      if (!validation.isValid) newErrors.password = validation.error;
    }

    // Confirm Password validation
    if (formData.password && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.addressLine1?.trim()) newErrors.addressLine1 = 'Address Line 1 is required';
    if (!formData.city?.trim()) newErrors.city = 'City is required';
    if (!formData.state || formData.state === 'Select') newErrors.state = 'State is required';
    
    if (!formData.zipCode?.trim()) {
      newErrors.zipCode = 'Zip Code is required';
    } else if (formData.zipCode.trim().length !== 5) {
      newErrors.zipCode = 'Zip code must be exactly 5 digits';
    }

    const emailErr = getEmailValidationError((formData.email || '').trim());
    if (emailErr) newErrors.email = emailErr;

    const homePhoneError = validateRequiredPhone(formData.phone, { required: false, label: 'Home Phone' });
    if (homePhoneError) newErrors.phone = homePhoneError;

    const cellPhoneError = validateRequiredPhone(formData.cellPhone, { label: 'Cell Phone' });
    if (cellPhoneError) newErrors.cellPhone = cellPhoneError;

    const faxError = validateRequiredPhone(formData.fax, { required: false, label: 'Fax' });
    if (faxError) newErrors.fax = faxError;

    const mergedErrors = preserveAvailabilityErrors(errors, newErrors);
    setErrors(mergedErrors);
    return Object.keys(mergedErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!validateForm()) {
      setIsSubmitting(false);
      toast.warn(VALIDATION_SUMMARY_MESSAGE);
      return;
    }

    const usernameOk = await ensureUsernameAvailable(formData.username, setErrors);
    const emailOk = await ensureEmailAvailable(formData.email, setErrors);
    if (!usernameOk || !emailOk) {
      setIsSubmitting(false);
      toast.warn(VALIDATION_SUMMARY_MESSAGE);
      return;
    }

      try {
        let result;
        // Filter out fields that the backend doesn't expect
        const filteredFormData = {
          first_name: formData.firstName || '',
          last_name: formData.lastName || '',
          middle_name: formData.middleInitial || '',
          email: formData.email || '',
          phone: formData.cellPhone || '',
          cell_phone: formData.phone || '',
          fax: formData.fax || '',
          address_line_1: formData.addressLine1 || '',
          address_line_2: formData.addressLine2 || '',
          city: formData.city || '',
          state_id: (formData.stateId !== undefined && formData.stateId !== null && formData.stateId !== '')
            ? parseInt(formData.stateId)
            : 1,
          zip_code: formData.zipCode ? String(formData.zipCode) : '',
          username: formData.username || '',
          status: formData.status || 'Active',
          two_way_auth: formData.twoWayAuth || 'Disabled',
          time_zone: formData.timeZone,
          title_id: parseInt(formData.title) || null
        };

        if (formData.password && formData.password.trim() !== '') {
          filteredFormData.password = formData.password.trim();
        }

        console.log('🔧 Filtered form data before sending:', filteredFormData);

        if (isEditMode) {
          result = await dispatch(updateAdmin({ 
            practiceId: isSystemAdmin ? null : practiceId, 
            adminId: editId, 
            adminData: filteredFormData 
          })).unwrap();
        } else {
          result = await dispatch(createAdmin({ 
            practiceId: isSystemAdmin ? null : practiceId, 
            adminData: {
              ...filteredFormData,
              password: formData.password?.trim() || ''
            } 
          })).unwrap();
        }

        if (isSystemAdmin) {
          toast.success(isEditMode ? 'System admin updated' : 'System admin added');
          navigate('/admin');
        } else {
          toast.success(isEditMode ? 'Practice admin updated' : 'Practice admin added');
          const fromDashboard = location.state?.fromDashboard;
          const path = fromDashboard ? `/dashboard/${practiceId}?tab=admin` : `/practices/${practiceId}?tab=admin`;
          navigate(path, { state: { fromDashboard } });
        }

      } catch (error) {
        console.error(`Error ${isEditMode ? 'updating' : 'creating'} admin:`, error);
        if (applyPasswordServerFieldError(setErrors, error)) {
          return;
        }
        const errorMessage = typeof error === 'string'
          ? error
          : (error.response?.data?.message || error.message || 'Failed to save admin. Please try again.');
        toast.error(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
  };

  const handleCancel = () => {
    if (isSystemAdmin) {
      navigate('/admin');
    } else {
      const fromDashboard = location.state?.fromDashboard;
      const path = fromDashboard ? `/dashboard/${practiceId}?tab=admin` : `/practices/${practiceId}?tab=admin`;
      navigate(path, { state: { fromDashboard } });
    }
  };


  // Get logo URL - construct full URL if logo path exists
  const getLogoUrl = () => {
    if (!currentPractice?.practice_logo) return null;
    const logo = currentPractice.practice_logo;
    if (logo.startsWith('http://') || logo.startsWith('https://')) return logo;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
    const baseUrl = API_URL.replace('/api', '');
    const logoPath = logo.startsWith('/') ? logo : `/${logo}`;
    return `${baseUrl}${logoPath}`;
  };

  const showEditLoading = isEditMode && (isSystemAdmin ? loadingAdmin : !currentAdmin);
  if (showEditLoading) {
    return (
      <div className="add-admin-main-container">
        <div className="add-admin-form-card" style={{ padding: '16px', display: 'grid', gap: '14px' }}>
          <FormDetailSkeleton titleLines={1} fieldRows={8} columns={2} showFooter={false} />
          <FormDetailSkeleton titleLines={1} fieldRows={2} columns={2} showFooter={true} />
        </div>
      </div>
    );
  }

  const adminTitle = isSystemAdmin
    ? (isEditMode ? 'Edit Admin' : 'Add Admin')
    : (isEditMode ? 'Edit Practice Admin' : 'Add Practice Admin');

  return (
    <>
    <div className="ap-container">
      <AddFormModel
        title={adminTitle}
        currentPractice={isSystemAdmin ? null : currentPractice}
        getLogoUrl={getLogoUrl}
        handleSubmit={handleSubmit}
        leftSection={
          <div className="ap-form-redesign-wrapper">
            <div className="pif-demo-section-label">Personal Information</div>

            <div className="ap-row ap-row-title-first">
              <div className="ap-field w-title">
                <label htmlFor="title">Title</label>
                <select id="title" name="title" value={formData.title} onChange={handleInputChange}>
                  <option value="">- Select -</option>
                  {TITLES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
                {errors.title && <span className="error-text">{errors.title}</span>}
              </div>
              <div className="ap-field w-first-name">
                <label htmlFor="firstName">First Name <span className="required">*</span></label>
                <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} placeholder="e.g., John" required />
                {errors.firstName && <span className="error-text">{errors.firstName}</span>}
              </div>
            </div>

            <div className="ap-row ap-row-mi-last">
              <div className="ap-field w-mi">
                <label htmlFor="middleInitial">Middle Name</label>
                <input type="text" id="middleInitial" name="middleInitial" value={formData.middleInitial} onChange={handleInputChange} maxLength={20} placeholder="e.g., Marie" />
              </div>
              <div className="ap-field w-last-name">
                <label htmlFor="lastName">Last Name <span className="required">*</span></label>
                <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} placeholder="e.g., Doe" required />
                {errors.lastName && <span className="error-text">{errors.lastName}</span>}
              </div>
            </div>

            <div className="ap-row ap-row-3col">
              <div className="ap-field">
                <label htmlFor="phone">Home Phone</label>
                <PhoneNumberInput id="phone" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="123-231-2345" />
                {errors.phone && <span className="error-text">{errors.phone}</span>}
              </div>
              <div className="ap-field">
                <label htmlFor="cellPhone">Cell Phone <span className="required">*</span></label>
                <PhoneNumberInput id="cellPhone" name="cellPhone" value={formData.cellPhone} onChange={handleInputChange} placeholder="123-231-2345" required />
                {errors.cellPhone && <span className="error-text">{errors.cellPhone}</span>}
              </div>
              <div className="ap-field">
                <label htmlFor="fax">Fax</label>
                <PhoneNumberInput id="fax" name="fax" value={formData.fax} onChange={handleInputChange} placeholder="123-231-2345" />
                {errors.fax && <span className="error-text">{errors.fax}</span>}
              </div>
            </div>

            <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Account Credentials</div>

            <div className="ap-row ap-row-2col">
              <div className="ap-field">
                <label htmlFor="username">Username <span className="required">*</span></label>
                <input type="text" id="username" name="username" value={formData.username} onChange={handleInputChange} onPaste={(e) => handleUsernamePaste(e, setErrors)} onBlur={() => handleUsernameBlur(formData.username, setErrors)} autoComplete="username" placeholder="e.g., john.doe" required maxLength={USERNAME_MAX_LENGTH} />
                {errors.username && <span className="error-text">{errors.username}</span>}
              </div>
              <div className="ap-field">
                <label htmlFor="email">Email <span className="required">*</span></label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} onPaste={(e) => handleEmailPaste(e, setErrors)} placeholder="e.g., admin@example.com" required />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>
            </div>

            <div className="ap-row ap-row-2col">
              <div className="ap-field">
                <label htmlFor="password">Password {!isEditMode && <span className="required">*</span>}</label>
                <div className="ap-password-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    autoComplete="new-password"
                    placeholder={isEditMode ? 'Leave blank to keep current' : 'Enter at least 8 characters'}
                  />
                  <button type="button" className="ap-password-toggle" onClick={togglePasswordVisibility} aria-label="Toggle password visibility">
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
                {errors.password && <span className="error-text">{errors.password}</span>}
              </div>
              <div className="ap-field">
                <label htmlFor="confirmPassword">Confirm Password {!isEditMode && <span className="required">*</span>}</label>
                <div className="ap-password-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    autoComplete="new-password"
                    placeholder={isEditMode ? 'Leave blank to keep current' : 'Enter at least 8 characters'}
                  />
                  <button type="button" className="ap-password-toggle" onClick={toggleConfirmPasswordVisibility} aria-label="Toggle confirm password visibility">
                    <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
                {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
              </div>
            </div>

            <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Address & Location</div>

            <div className="ap-field full-width">
              <label htmlFor="addressLine1">Address Line 1 <span className="required">*</span></label>
              <input type="text" id="addressLine1" name="addressLine1" value={formData.addressLine1} onChange={handleInputChange} placeholder="e.g., 123 Main St" required />
              {errors.addressLine1 && <span className="error-text">{errors.addressLine1}</span>}
            </div>

            <div className="ap-field full-width">
              <label htmlFor="addressLine2">Address Line 2</label>
              <input type="text" id="addressLine2" name="addressLine2" value={formData.addressLine2} onChange={handleInputChange} placeholder="e.g., Apt 4B" />
            </div>

            <div className="ap-row ap-row-3col">
              <div className="ap-field">
                <label htmlFor="city">City <span className="required">*</span></label>
                <input type="text" id="city" name="city" value={formData.city} onChange={handleInputChange} placeholder="e.g., New York" required />
                {errors.city && <span className="error-text">{errors.city}</span>}
              </div>
              <div className="ap-field">
                <label htmlFor="state">State <span className="required">*</span></label>
                <select id="state" name="state" value={formData.state} onChange={handleInputChange} required>
                  <option value="">Select State</option>
                  {US_STATES.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                </select>
                {errors.state && <span className="error-text">{errors.state}</span>}
              </div>
              <div className="ap-field">
                <label htmlFor="zipCode">Zip Code <span className="required">*</span></label>
                <input type="text" id="zipCode" name="zipCode" value={formData.zipCode} onChange={handleInputChange} placeholder="e.g., 10001" maxLength="5" required />
                {errors.zipCode && <span className="error-text">{errors.zipCode}</span>}
              </div>
            </div>

            <div className="ap-field full-width">
              <label htmlFor="timeZone">Time Zone</label>
              <select id="timeZone" name="timeZone" value={formData.timeZone} onChange={handleInputChange}>
                <option value="">Select Time Zone</option>
                {TIME_ZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
              {errors.timeZone && <span className="error-text">{errors.timeZone}</span>}
            </div>

            <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Additional Settings</div>

            <div className="ap-row ap-row-2col">
              <div className="ap-field">
                <label htmlFor="status">Status <span className="required">*</span></label>
                <select id="status" name="status" value={formData.status || 'Active'} onChange={handleInputChange}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
                {errors.status && <span className="error-text">{errors.status}</span>}
              </div>
              <div className="ap-field">
                <label htmlFor="twoWayAuth">Two Way Authentication <span className="required">*</span></label>
                <select id="twoWayAuth" name="twoWayAuth" value={formData.twoWayAuth} onChange={handleInputChange}>
                  <option value="Disabled">Disabled</option>
                  <option value="Enabled">Enabled</option>
                </select>
                {errors.twoWayAuth && <span className="error-text">{errors.twoWayAuth}</span>}
              </div>
            </div>
          </div>
        }
        rightSection={null}
        footerActions={
          <FormActionButtons
            onCancel={handleCancel}
            disabled={isSubmitting || isCreating || isUpdating || (isEditMode && !isChanged)}
            isSubmitting={isSubmitting || isCreating || isUpdating}
            isEditMode={isEditMode}
            addLabel="Save Admin"
            updateLabel="Save Changes"
          />
        }
      />
    </div>



    {/* Validation Warning Modal */}
    <Modal 
      show={showValidationModal} 
      onHide={() => setShowValidationModal(false)} 
      centered 
      className="add-admin-validation-modal"
    >
      <Modal.Body className="add-admin-modal-body" style={{ padding: '30px', textAlign: 'center' }}>
        <div className="add-admin-modal-icon-wrapper" style={{ marginBottom: '20px' }}>
          <FontAwesomeIcon icon={faInfoCircle} className="add-admin-modal-icon" style={{ fontSize: '40px', color: '#1A252F' }} />
        </div>
        <h4 className="add-admin-modal-title" style={{ color: '#1A252F', marginBottom: '20px', fontWeight: '600' }}>Fill all required fields</h4>
        <button 
          type="button"
          className="add-admin-modal-btn" 
          onClick={() => setShowValidationModal(false)}
          style={{ backgroundColor: '#1A252F', color: '#fff', border: 'none', padding: '10px 30px', borderRadius: '4px', cursor: 'pointer', width: '100%' }}
        >
          OK
        </button>
      </Modal.Body>
    </Modal>
  </>
  );
};

export default AddAdmin;